import { errorText, tx } from "@/lib/i18n";
import { useLocale } from "@/lib/i18n-react";
import type { ReactNode } from "react";
import {
  AlertCircle,
  ArrowUpRight,
  BarChart3,
  LoaderCircle,
} from "lucide-react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { cn } from "@/lib/utils";

export function PageHeading({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description: string;
  actions?: ReactNode;
}) {
  useLocale();
  return (
    <div className="mb-7 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
      <div>
        {eyebrow && <p className="eyebrow mb-2">{eyebrow}</p>}
        <h1 className="text-[28px] font-bold leading-tight tracking-tight sm:text-[30px]">
          {title}
        </h1>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
          {description}
        </p>
      </div>
      {actions && (
        <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>
      )}
    </div>
  );
}
export function Loading({
  label = tx("Preparing your results…"),
  className,
}: {
  label?: string;
  className?: string;
}) {
  useLocale();
  return (
    <div
      role="status"
      className={cn(
        "flex min-h-48 items-center justify-center gap-2 text-sm text-muted-foreground",
        className,
      )}
    >
      <LoaderCircle className="size-4 animate-spin" />
      {tx(label)}
    </div>
  );
}
export function QueryError({ message }: { message: string }) {
  const isEnglish = useLocale() === "en";
  const translated = errorText(message);
  return (
    <div
      role="alert"
      className="flex items-start gap-2 rounded-lg border border-destructive/20 bg-destructive/5 p-4 text-sm text-destructive"
    >
      <AlertCircle className="mt-0.5 size-4 shrink-0" />
      <div>
        {translated !== message || isEnglish ? (
          translated
        ) : (
          <>
            {tx("Unable to run this query.")}
            <details className="mt-2">
              <summary>{tx("Technical details")}</summary>
              <pre className="mt-2 max-w-full overflow-auto whitespace-pre-wrap break-words">
                {message}
              </pre>
            </details>
          </>
        )}
      </div>
    </div>
  );
}
export function Empty({
  title = tx("No results for this selection"),
  description = tx("Try a different property, period or report filter."),
}: {
  title?: string;
  description?: string;
}) {
  useLocale();
  return (
    <div className="flex min-h-48 flex-col items-center justify-center px-5 text-center">
      <div className="mb-3 rounded-xl bg-muted p-3">
        <BarChart3 className="size-5 text-muted-foreground" />
      </div>
      <p className="text-sm font-semibold">{tx(title)}</p>
      <p className="mt-1 text-sm text-muted-foreground">{tx(description)}</p>
    </div>
  );
}
export function StatusBadge({ status }: { status: string }) {
  useLocale();
  return (
    <Badge
      variant={
        status === "Ready"
          ? "success"
          : status === "Blocked"
            ? "danger"
            : status === "Inspection"
              ? "warning"
              : "outline"
      }
      className="min-h-6 whitespace-nowrap px-2 py-0.5 text-[11px]"
    >
      <span
        className={cn(
          "size-1.5 rounded-full",
          status === "Ready"
            ? "bg-emerald-500"
            : status === "Blocked"
              ? "bg-rose-500"
              : status === "Inspection"
                ? "bg-amber-500"
                : "bg-sky-500",
        )}
      />
      {tx(status)}
    </Badge>
  );
}
export function ActionCard({
  title,
  description,
  onClick,
  children,
}: {
  title: string;
  description: string;
  onClick: () => void;
  children?: ReactNode;
}) {
  useLocale();
  return (
    <Card className="flex flex-col gap-2 p-5">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">{title}</h3>
        <Button
          size="icon"
          variant="ghost"
          aria-label={tx("Open {p0}", { p0: title })}
          onClick={onClick}
        >
          <ArrowUpRight />
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">{description}</p>
      {children}
    </Card>
  );
}
